import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAsideMenu } from "../../contexts/AsideMenuContext";
import { getSettings } from "../../services/settingsServices";

import AsideMenuAdmin from "./AsideMenu";
import TopBarAdmin from "./TopBarAdmin";

import Dashboard from "./AppliAdmin/Dashboard/Dashboard";
import Actualites from "./AppliAdmin/Actualites/Actualites";
import AlbumsPhotos from "./AppliAdmin/AlbumsPhotos/AlbumsPhotos";
import Partenaires from "./AppliAdmin/Partenaires/Partenaires";
import Disciplines from "./AppliAdmin/Disciplines/Disciplines";
import Groupes from "./AppliAdmin/Groupes/Groupes";
import LeClub from "./AppliAdmin/LeClub/LeClub";
import Inscriptions from "./AppliAdmin/Inscriptions/Inscriptions";
import Parametres from "./AppliAdmin/Parametres/Parametres";
import ErrorPage from "./../ErrorPage/ErrorPage";

const DEFAULT_CLUB = {
  logo: "",
  nom: "MonAppliClub Light",
};

const DEFAULT_USER = {
  id: 0,
  nom: "",
  prenom: "",
  email: "",
  photoURL: "",
  role_id: 0,
};

const ADMIN_ROUTES = [
  {
    path: "dashboard",
    title: "Tableau de bord",
    subtitle:
      "Vue d’ensemble de votre club, de vos contenus et des actions à traiter.",
    element: <Dashboard />,
  },
  {
    path: "inscriptions",
    title: "Inscriptions",
    subtitle: "Suivez les demandes d’inscription et les nouveaux adhérents.",
    element: <Inscriptions />,
  },
  {
    path: "disciplines",
    title: "Disciplines",
    subtitle:
      "Gérez les disciplines, horaires et informations affichées côté visiteur.",
    element: <Disciplines />,
  },
  {
    path: "actualites",
    title: "Actualités",
    subtitle: "Publiez, modifiez et organisez les actualités du club.",
    element: <Actualites />,
  },
  {
    path: "albums-photos",
    title: "Albums photos",
    subtitle: "Mettez en valeur la vie du club avec vos galeries.",
    element: <AlbumsPhotos />,
  },
  {
    path: "partenaires",
    title: "Partenaires",
    subtitle: "Administrez les partenaires et soutiens du club.",
    element: <Partenaires />,
  },
  {
    path: "groupes",
    title: "Groupes",
    subtitle: "Structurez les groupes, publics et niveaux de pratique.",
    element: <Groupes />,
  },
  {
    path: "le-club",
    title: "Le Club",
    subtitle:
      "Présentez l’identité, l’équipe et les informations importantes du club.",
    element: <LeClub />,
  },
  {
    path: "parametres",
    title: "Paramètres",
    subtitle:
      "Configurez les informations générales, le logo et les préférences du site.",
    element: <Parametres />,
  },
];

const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export default function AdminLayout() {
  const { isOpen, toggleMenu } = useAsideMenu();
  const location = useLocation();

  const [clubInfo, setClubInfo] = useState(DEFAULT_CLUB);
  const [userInfo, setUserInfo] = useState(DEFAULT_USER);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const [unreadCounts, setUnreadCounts] = useState({
    notifications: 0,
    messages: 0,
  });

  const currentRoute = useMemo(() => {
    const cleanPath = location.pathname.replace(/^\/appli\/?/, "");

    return (
      ADMIN_ROUTES.find((route) => cleanPath.startsWith(route.path)) ||
      ADMIN_ROUTES[0]
    );
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;

    const fetchClubSettings = async () => {
      try {
        const settings = await getSettings();
        const club = Array.isArray(settings) ? settings[0] : settings;

        if (!mounted || !club) return;

        setClubInfo({
          logo: club.logo_club || "",
          nom: club.nom_club || DEFAULT_CLUB.nom,
        });
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des paramètres du club :",
          error
        );
      }
    };

    fetchClubSettings();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const shouldUseDark = savedTheme === "dark";

    document.documentElement.classList.toggle("dark", shouldUseDark);
    setIsDarkMode(shouldUseDark);

    const storedUser =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null) ||
      DEFAULT_USER;

    setUserInfo({
      ...DEFAULT_USER,
      ...storedUser,
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;

      document.documentElement.classList.toggle("dark", next);
      localStorage.setItem("theme", next ? "dark" : "light");

      return next;
    });
  }, []);

  const refreshUnreadCounts = useCallback(async () => {
    try {
      /*
        Tu pourras brancher ici tes vrais services plus tard.

        Exemple :
        const notifications = await getUnreadNotificationsCount();
        const messages = await getUnreadContactMessagesCount();

        setUnreadCounts({
          notifications: notifications?.count || notifications || 0,
          messages: messages?.count || messages || 0,
        });
      */

      setUnreadCounts({
        notifications: 0,
        messages: 0,
      });
    } catch (error) {
      console.error("Erreur lors du chargement des compteurs non lus :", error);

      setUnreadCounts({
        notifications: 0,
        messages: 0,
      });
    }
  }, []);

  useEffect(() => {
    refreshUnreadCounts();
  }, [refreshUnreadCounts]);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 transition-colors duration-300 dark:bg-slate-950 dark:text-slate-50">
      <AsideMenuAdmin
        isOpen={isOpen}
        toggleMenu={toggleMenu}
        clubInfo={clubInfo}
        userInfo={userInfo}
        isDarkMode={isDarkMode}
        onToggleTheme={toggleTheme}
        unreadCounts={unreadCounts}
      />

      {isOpen && (
        <button
          type="button"
          aria-label="Fermer le menu"
          onClick={toggleMenu}
          className="fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-sm lg:hidden"
        />
      )}

      <div
        className={`min-h-screen transition-all duration-300 ${
          isOpen ? "lg:pl-72" : "lg:pl-24"
        }`}
      >
        <TopBarAdmin
          isMenuOpen={isOpen}
          toggleMenu={toggleMenu}
          clubInfo={clubInfo}
          userInfo={userInfo}
          title={currentRoute.title}
          subtitle={currentRoute.subtitle}
          isDarkMode={isDarkMode}
          onToggleTheme={toggleTheme}
          unreadCounts={unreadCounts}
          onRefreshBadges={refreshUnreadCounts}
        />

        <main className="mx-auto w-full max-w-[1600px] px-4 pb-8 pt-5 sm:px-6 lg:px-8">
          <Routes>
            <Route index element={<Navigate to="dashboard" replace />} />

            {ADMIN_ROUTES.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}

            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}